import { ExternalLink } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface Scanner {
  name: string;
  baseUrl: string;
  icon?: string;
}

interface BalanceDisplayProps {
  balance: number | null;
  address: string;
  publicKey: string | null;
  preferredScanner: Scanner;
}

export function BalanceDisplay({
  balance,
  address,
  publicKey,
  preferredScanner,
}: BalanceDisplayProps) {
  if (balance === null) return null;

  const displayAddress = address.trim() || publicKey;
  const addressType = address.trim() ? "Entered Address" : "Connected Wallet";

  return (
    <div className="bg-gradient-to-r from-[#ffffff]/5 to-[#ffffff]/[0.07] rounded-lg p-4 space-y-3 border border-[#ffffff]/10">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-sm text-[#ffffff]/70">{addressType}</span>
        </div>
        {displayAddress && (
          <a
            href={`${preferredScanner.baseUrl}/account/${displayAddress}?cluster=devnet`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#9945FF] hover:text-[#9945FF]/80 flex items-center gap-1 transition-colors shrink-0"
          >
            View
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="font-mono text-sm text-[#ffffff]/70 cursor-help truncate">
                  {displayAddress
                    ? `${displayAddress.slice(0, 8)}...${displayAddress.slice(
                        -8
                      )}`
                    : ""}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-mono text-xs">{displayAddress}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Casino-style Balance Display */}
        <div className="relative overflow-hidden rounded-lg bg-[#000000]/40 p-4 border border-[#ffffff]/10">
          <div className="absolute inset-0 bg-gradient-to-r from-[#9945FF]/10 via-[#ffffff]/5 to-[#00D18C]/10 animate-pulse" />
          <div className="relative flex flex-col items-center gap-1">
            <div className="text-xs text-[#ffffff]/50 font-medium uppercase tracking-wider">
              Current Balance
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold font-mono bg-gradient-to-r from-[#9945FF] to-[#00D18C] bg-clip-text text-transparent">
                {balance.toFixed(4)}
              </span>
              <span className="text-lg font-semibold text-[#ffffff]/90">
                SOL
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className="h-px w-12 bg-gradient-to-r from-transparent via-[#ffffff]/20 to-transparent" />
              <span className="text-xs text-[#ffffff]/40 font-medium">
                DEVNET
              </span>
              <div className="h-px w-12 bg-gradient-to-r from-transparent via-[#ffffff]/20 to-transparent" />
            </div>
          </div>
          <div className="absolute top-0 right-0 h-full w-24 bg-gradient-to-l from-[#ffffff]/5 to-transparent" />
          <div className="absolute top-0 left-0 h-full w-24 bg-gradient-to-r from-[#ffffff]/5 to-transparent" />
        </div>
      </div>
    </div>
  );
}
